/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Box,
  ListItemIcon,
  IconButton,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Slide from '@mui/material/Slide';
import type { TransitionProps } from '@mui/material/transitions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useAddToCartMutation } from 'renderer/features/payments-page/services/cart.endpoints';

interface PurchaseDialogForProductDetailsPageProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  productId: string | null;
  products: any[];
  platform: string;
  hostApp: any;
  quantity?: number;
  redirectToPurchasePage?: boolean;
  fromAddToCart?: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const toHostAppPayload = (ha: any | null) => {
  if (!ha) return undefined;
  const priceOverride =
    typeof ha.priceOverride === 'number' ? ha.priceOverride : undefined;
  return {
    id: ha.id,
    displayName: ha.displayName,
    version: ha.version,
    ...(priceOverride !== undefined ? { priceOverride } : {}),
  };
};

const PurchaseDialogForProductDetailsPage: React.FC<
  PurchaseDialogForProductDetailsPageProps
> = ({
  open,
  onClose,
  onNavigate,
  productId,
  products,
  platform,
  hostApp,
  redirectToPurchasePage,
  quantity,
  fromAddToCart,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [addToCart] = useAddToCartMutation();

  // Normalize input but DO NOT early-return; keep hook order stable.
  const safeProducts = Array.isArray(products) ? products : [];
  const [product] = safeProducts.filter((p) => p._id === productId);
  const subscriptions = safeProducts.filter(
    (p) => p.category === 'Subscription',
  );

  const [singleHostApp] = subscriptions.filter(
    (p) => p.name?.toLowerCase().includes('single host'),
  );
  const [allAccess] = subscriptions.filter(
    (p) => p.name?.toLowerCase().includes('all access'),
  );

  const haveData = safeProducts.length > 0;

  // Shared add + navigate helper (always defined)
  const handleAdd = useCallback(
    async (
      payload: {
        productId: string | null;
        quantity?: number;
        platform?: string;
        hostApplication?:
          | string
          | {
              id: string;
              displayName: string;
              version: string;
              priceOverride?: number;
            };
      },
      navigateTo: string,
    ) => {
      if (!payload.productId) return;
      await addToCart({
        productId: payload.productId,
        quantity: payload.quantity ?? 1,
        platform: payload.platform,
        hostApplication: payload.hostApplication,
      });
      onNavigate(navigateTo);
    },
    [addToCart, onNavigate],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="purchase-dialog-title"
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      keepMounted
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        aria-label="Close purchase dialog"
        sx={{ position: 'absolute', top: 8, right: 8, p: 1.25, zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          backgroundColor: '#f9f9f9',
          py: { xs: 2, sm: 5 },
          px: { xs: 2, sm: 4 },
          overflowY: 'auto',
        }}
      >
        <Typography
          id="purchase-dialog-title"
          variant={isMobile ? 'h6' : 'h5'}
          fontWeight={700}
          textAlign="center"
          mb={{ xs: 1.5, sm: 2 }}
          component="div" // <-- avoid nested <p> when this wraps rich content later
        >
          Choose Your Purchase
        </Typography>

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ opacity: 0.8, mb: { xs: 2, sm: 3 } }}
          component="div" // <-- safe
        >
          Compare options and continue.
        </Typography>

        {/* Empty state (keeps hooks stable) */}
        {!haveData ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" component="div" sx={{ opacity: 0.7 }}>
              Loading product options…
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 4 }} alignItems="stretch">
            {/* Subscription Card */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #c8e6c9',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    component="div" // <-- safe
                    sx={{
                      backgroundColor: '#66bb6a',
                      color: 'white',
                      px: 1.2,
                      py: 0.3,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      display: 'inline-block',
                      mb: 1,
                    }}
                  >
                    Most Popular
                  </Typography>

                  <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    align="center"
                    fontWeight={700}
                    mb={1.5}
                    color="primary"
                    component="div" // <-- safe
                  >
                    Subscription
                  </Typography>

                  <List
                    dense
                    sx={{
                      '& .MuiListItemIcon-root': { minWidth: 32 },
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}
                  >
                    {[
                      'Access to all our plugins for 12 months.',
                      'New plugins included at no extra cost.',
                      'Unlimited phone & email support.',
                      'Includes updates and upgrades.',
                    ].map((text, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ sx: { color: 'black' } }}
                          primary={text}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box mt="auto" display="flex" flexDirection="column" gap={1.25}>
                  <Button
                    fullWidth
                    variant="contained"
                    size={isMobile ? 'large' : 'medium'}
                    sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                    disabled={!singleHostApp}
                    onClick={() =>
                      singleHostApp &&
                      handleAdd(
                        {
                          productId: singleHostApp._id,
                          platform:
                            singleHostApp.supportedPlatforms?.[0] ??
                            'All Platforms',
                        },
                        `/cart/${singleHostApp._id}`,
                      )
                    }
                  >
                    Buy One Host App
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    size={isMobile ? 'large' : 'medium'}
                    sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                    disabled={!allAccess}
                    onClick={() =>
                      allAccess &&
                      handleAdd(
                        {
                          productId: allAccess._id,
                          platform:
                            allAccess.supportedPlatforms?.[0] ??
                            'All Platforms',
                          hostApplication:
                            allAccess.supportedHostApplications?.[0],
                        },
                        `/cart/${allAccess._id}`,
                      )
                    }
                  >
                    Buy All Access
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Perpetual License Card */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  backgroundColor: '#fafafa',
                  color: '#333',
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #ddd',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box>
                  <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    align="center"
                    fontWeight={700}
                    mb={1.5}
                    mt={{ xs: 1, sm: 2 }}
                    component="div" // <-- safe
                  >
                    Perpetual License
                  </Typography>

                  <List
                    dense
                    sx={{
                      '& .MuiListItemIcon-root': { minWidth: 32 },
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}
                  >
                    {[
                      'One year of free updates and upgrades.',
                      'Plugin-only: includes a single plugin.',
                      'Phone & email support for one year.',
                      'Upgrade fees may apply later.',
                    ].map((text, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText primary={text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box mt="auto">
                  <Button
                    fullWidth
                    variant="contained"
                    size={isMobile ? 'large' : 'medium'}
                    sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                    disabled={!productId && !product}
                    onClick={() => {
                      const targetId = productId ?? product?._id ?? null;
                      const path = redirectToPurchasePage
                        ? `/purchase`
                        : `/cart/${targetId}`;

                      if (fromAddToCart) {
                        // Parent will add & show confirmation when triggerAddToCartAfterDialog is true
                        onNavigate(path);
                      } else {
                        // Dialog handles add, then navigates
                        handleAdd(
                          {
                            productId: targetId,
                            quantity: quantity || 1,
                            platform:
                              platform ||
                              product?.supportedPlatforms?.[0] ||
                              'All Platforms',
                            // hostApplication: hostApp,
                            hostApplication: toHostAppPayload(hostApp),
                          },
                          path,
                        );
                      }
                    }}
                  >
                    {fromAddToCart ? 'Add Plugin Only' : 'Buy Plugin'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {isMobile && <Divider sx={{ mt: 2 }} />}
      </DialogContent>

      <DialogActions
        sx={{
          position: { xs: 'sticky', sm: 'static' },
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: { xs: '1px solid', sm: 'none' },
          borderColor: { xs: 'divider', sm: 'transparent' },
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={onClose}
          color="primary"
          variant="text"
          size={isMobile ? 'large' : 'medium'}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PurchaseDialogForProductDetailsPage.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  productId: PropTypes.string,
};

export default PurchaseDialogForProductDetailsPage;
